import React, { useState, useEffect } from 'react';
import { X, Gift, Loader2, Mail, Copy, Check } from 'lucide-react';
import axios from 'axios';

const STORAGE_KEY = 'jrf_welcome_10pct_v1';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WelcomeModal = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('intro'); // intro | loading | success
  const [email, setEmail] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  const dontMiss = () => {
    // Permite cerrar sin guardar la bandera, para que vuelva a aparecer en próximas visitas
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Introduce un email válido');
      return;
    }
    setError('');
    setStage('loading');
    try {
      const reg = await axios.post(
        `${API}/loyalty/register?email=${encodeURIComponent(value)}&name=`
      );
      const code = reg.data?.welcome_coupon;
      const exp = reg.data?.welcome_coupon_expiry;
      if (code) {
        setCoupon(code);
        setExpiry(exp);
      } else {
        // El email ya estaba registrado antes → informar
        setCoupon(null);
        setExpiry(null);
      }
      setStage('success');
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      setError('No se pudo generar el cupón. Prueba de nuevo en un momento.');
      setStage('intro');
    }
  };

  const copyCode = async () => {
    if (!coupon) return;
    try {
      await navigator.clipboard.writeText(coupon);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  if (!open) return null;

  const expiryDate = expiry
    ? new Date(expiry).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 animate-fadeIn"
      onClick={dontMiss}
      data-testid="welcome-modal-overlay"
    >
      <div
        className="relative bg-white text-black max-w-md w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-modal"
      >
        {/* Close */}
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 bg-white/80 backdrop-blur-sm p-1.5 text-black hover:bg-white transition-colors"
          data-testid="welcome-modal-close"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>

        {/* HERO — bloque negro con la oferta */}
        <div className="bg-black text-white px-8 pt-10 pb-7 text-center relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5 blur-2xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white/5 blur-2xl"
          />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white text-black mb-4">
              <Gift className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60 mb-2">
              Solo para nuev@s miembr@s
            </p>
            <h2 className="text-5xl sm:text-6xl font-black tracking-tighter leading-none mb-2">
              -10%
            </h2>
            <p className="text-sm text-white/80">
              en tu primera compra
            </p>
          </div>
        </div>

        {/* BODY */}
        <div className="px-8 py-7">
          {stage === 'intro' && (
            <>
              <p className="text-sm text-neutral-700 leading-relaxed mb-5 text-center">
                Déjanos tu email y te enviamos un <strong className="text-black">código de 10% de descuento</strong> para que estrenes la colección.
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                    strokeWidth={1.5}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    autoComplete="email"
                    required
                    className="w-full border border-neutral-300 pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                    data-testid="welcome-email-input"
                  />
                </div>
                {error && (
                  <p className="text-xs text-red-600" data-testid="welcome-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  className="w-full bg-black text-white hover:bg-neutral-800 py-3.5 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                  data-testid="welcome-submit"
                >
                  Conseguir mi 10%
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="w-full text-[11px] text-neutral-500 hover:text-black uppercase tracking-widest pt-1"
                  data-testid="welcome-skip"
                >
                  No, gracias
                </button>
              </form>
              <p className="text-[10px] text-neutral-500 text-center mt-5 leading-relaxed">
                Al continuar aceptas recibir el código por email. Puedes darte de baja en cualquier momento.
              </p>
            </>
          )}

          {stage === 'loading' && (
            <div className="py-10 text-center">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-black" />
              <p className="text-sm text-neutral-600">Generando tu código…</p>
            </div>
          )}

          {stage === 'success' && (
            <div className="text-center" data-testid="welcome-success">
              {coupon ? (
                <>
                  <p className="text-sm text-neutral-700 mb-4">
                    ¡Listo! Guardamos tu código y te lo mandamos también por email a <strong className="text-black break-all">{email}</strong>.
                  </p>
                  <div
                    className="border-2 border-dashed border-black p-4 mb-4 select-all"
                    data-testid="welcome-coupon-code"
                  >
                    <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">
                      Tu código
                    </p>
                    <p className="text-xl font-black tracking-widest">{coupon}</p>
                    {expiryDate && (
                      <p className="text-[11px] text-neutral-500 mt-2">
                        Un solo uso · Caduca el <strong className="text-black">{expiryDate}</strong>
                      </p>
                    )}
                  </div>
                  <button
                    onClick={copyCode}
                    className="w-full bg-black text-white hover:bg-neutral-800 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors flex items-center justify-center gap-2"
                    data-testid="welcome-copy"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4" strokeWidth={2.5} /> Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" strokeWidth={2} /> Copiar código
                      </>
                    )}
                  </button>
                  <button
                    onClick={close}
                    className="w-full border border-neutral-300 text-black hover:border-black py-3 text-xs font-bold uppercase tracking-[0.25em] mt-2 transition-colors"
                    data-testid="welcome-shop"
                  >
                    Empezar a comprar
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-neutral-700 mb-5">
                    Este email ya está registrado 💌 Si todavía tienes tu código de bienvenida, úsalo en el checkout. Si no, consulta tu bandeja de entrada.
                  </p>
                  <button
                    onClick={close}
                    className="w-full bg-black text-white hover:bg-neutral-800 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors"
                  >
                    Entendido
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Firma */}
        <div className="px-8 pb-6">
          <p className="text-[11px] text-neutral-500 italic text-center border-t border-neutral-200 pt-4">
            — Jeilyn, fundadora de JRF Sportswear
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn > div {
          animation: fadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
};

export default WelcomeModal;
